import { useEffect, useState, useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import { URLS } from 'services/urls';
import { NS_LOGO_URL } from 'constants/branding';
import { DealLite } from 'views/Deals/hooks/useGetOrganizationDealsLite';
import { OrganizationSubproject } from 'views/ProFormaTable/types';
import { coverPictureToIcon } from '../helpers';
import { SIDE_NAV_TEST_IDS, SEARCH_DEBOUNCE_MS } from '../../../constants';
import ProjectsDealsMenuItem from '../ProjectsDealsMenuItem/ProjectsDealsMenuItem';
import { NSSearchInput } from 'bricks';
import './ProjectsDealsMenu.scss';
import { NSThemeProvider } from '@northspyre-inc/ui';
import { matchesProjectSearch, matchesDealSearch } from '../helpers';

interface IProjectsDealsMenuProps {
    projects?: OrganizationSubproject[];
    deals?: DealLite[];
    currentDealId?: number;
    onSelect?: () => void;
}

export const buildDealSubtitle = (deal: Pick<DealLite, 'city' | 'state'>): string => (
    [deal.city, deal.state].filter(Boolean).join(', ')
);

const ProjectsDealsMenu = ({
    projects, deals, currentDealId, onSelect,
}: IProjectsDealsMenuProps) => {
    const hasProjects = Boolean(projects && projects.length > 0);
    const hasDeals = Boolean(deals && deals.length > 0);

    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setAppliedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    const filteredProjects = useMemo(
        () => projects?.filter(project => matchesProjectSearch(project, appliedSearch)) || [],
        [projects, appliedSearch],
    );
    const filteredDeals = useMemo(
        () => (deals || []).filter(deal => matchesDealSearch(deal, appliedSearch)),
        [deals, appliedSearch],
    );

    const showProjects = hasProjects && filteredProjects.length > 0;
    const showDeals = hasDeals && filteredDeals.length > 0;
    const hasNoResults = (hasProjects || hasDeals) && filteredProjects.length === 0 && filteredDeals.length === 0;

    return (
        <NSThemeProvider mode="dark" className="tw-h-full">
            <div
                className="ProjectsDealsMenu overflow-auto pb-2"
                data-testid={SIDE_NAV_TEST_IDS.SWITCHER_DROPDOWN}
            >
                {(hasProjects || hasDeals) && (
                    <div className='ProjectsDealsMenu p-2 sticky-top'>
                        <NSSearchInput
                            value={search}
                            onChange={setSearch}
                            placeholder="Search projects and deals..."
                            className="NSSearchInput--card mt-1"
                        />
                    </div>
                )}
                <div role="menu">
                    {showProjects && (
                        <div role="group" aria-label="Projects">
                            <div className="ProjectsDealsMenu__section-label font-10 pl-2 pb-1 pt-1">
                                PROJECTS
                            </div>
                            {filteredProjects.map(project => (
                                <ProjectsDealsMenuItem
                                    key={`project-${project.subprojectId}`}
                                    to={URLS.PROJECTS_APP.PROJECT_DASHBOARD_V2(project.subprojectId)}
                                    isExternal
                                    imgSrc={coverPictureToIcon(project.subproject.coverPicture)}
                                    imgAlt="Project"
                                    name={project.subproject.subprojectName}
                                    subtitle={project.subproject.developmentProject.projectName}
                                    title={project.subproject.subprojectName}
                                    testId={SIDE_NAV_TEST_IDS.SWITCHER_PROJECT_ITEM}
                                    onClick={onSelect}
                                />
                            ))}
                        </div>
                    )}

                    {showDeals && (
                        <div role="group" aria-label="Deals">
                            <div className="ProjectsDealsMenu__section-label font-10 pl-2 pb-1 pt-1">
                                DEALS
                            </div>
                            {filteredDeals.map(deal => (
                                <ProjectsDealsMenuItem
                                    key={`deal-${deal.id}`}
                                    to={generatePath(URLS.DEAL_DASHBOARD.HOME, { dealId: String(deal.id) })}
                                    imgSrc={NS_LOGO_URL}
                                    imgAlt="Northspyre"
                                    name={deal.dealName}
                                    subtitle={buildDealSubtitle(deal)}
                                    title={deal.dealName}
                                    testId={SIDE_NAV_TEST_IDS.SWITCHER_DEAL_ITEM}
                                    isActive={deal.id === currentDealId}
                                    onClick={onSelect}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {!hasProjects && !hasDeals && (
                    <div className="p-2 font-10 text-center">
                        No projects or deals available
                    </div>
                )}
                {hasNoResults && (
                    <div className="p-2 font-10 text-center">
                        No results found
                    </div>
                )}
            </div>
        </NSThemeProvider>
    );
};

export default ProjectsDealsMenu;
